import { useCallback, useRef, useState } from "react";
import { getSmsError, sendLeadSms, type TSmsError } from "@/services/sms.service";
import type { TInteraction } from "@/services/email.service";
import { newIdempotencyKey } from "@/utils/idempotency";
import { requireOutboundNumber } from "@/store/phone-numbers/caller-id.store";

/**
 * One click, one text. `sendingRef` blocks a second send synchronously — a
 * double click lands before React re-renders the disabled button — and the
 * idempotency key lets the server drop a repeat that still gets through.
 *
 * The key belongs to the draft: kept when the outcome is unknown (502 /
 * network) so a retry can't send twice, dropped after a success, a definite
 * failure, or an edit (`resetKey`), since that's a new message.
 */
export function useSendSms(leadId: number) {
  const sendingRef = useRef(false);
  const keyRef = useRef<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<TSmsError | null>(null);

  const send = useCallback(
    async (body: string): Promise<TInteraction | null> => {
      if (sendingRef.current) return null;
      sendingRef.current = true;
      setSending(true);
      setError(null);
      keyRef.current ??= newIdempotencyKey();
      try {
        // Pre-flight: without a number to text from (the agent's or the company
        // line) the backend refuses with NO_CALLER_ID — catch it before sending
        // and let the set-up-a-number prompt explain instead of an inline error.
        if (!(await requireOutboundNumber())) return null;
        const sent = await sendLeadSms(leadId, body, keyRef.current);
        keyRef.current = null;
        return sent;
      } catch (e) {
        const smsError = getSmsError(e);
        if (!smsError.uncertain) keyRef.current = null;
        setError(smsError);
        return null;
      } finally {
        sendingRef.current = false;
        setSending(false);
      }
    },
    [leadId]
  );

  const resetKey = useCallback(() => {
    keyRef.current = null;
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { send, sending, error, clearError, resetKey };
}
